import {
  ActionError,
  Button,
  Checkbox,
  ChoiceRow,
  EmptyState,
  Radio,
  RadioGroup,
  SearchInput,
  Skeleton,
  StatusTag,
  WaitPhrase,
} from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { errorMessage } from '@/lib/errors'
import { isUsableClinic, roleLabel, useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import { useUserClinics, type UserClinic } from './api'
import { AuthLayout, authLink } from './auth-layout'

/**
 * Escolha da clínica ativa (docs/areas/autenticacao.md, "Login, escolha de clínica e dashboard";
 * RN-AUT-11, P-AUT-02). Quem tem vínculo em mais de uma clínica (ou é root) escolhe onde entrar logo
 * depois do login: a padrão vem marcada e a linha inteira é clicável. Root busca entre todas as
 * clínicas e tem a visão global no topo. A escolha é `PUT /me/clinic` seguido da renovação do token
 * (`session.switchClinic`); até o token novo chegar, a tela não navega. Segue o leiaute do login
 * (P-AUT-12); o que a especificação mínima não decide está em P-AUT-21.
 */

/** O valor da opção "Visão global" no grupo de escolha: root sem clínica ativa (RT-03). */
const GLOBAL_VIEW = 'global'

interface Choice {
  /** `null` é a visão global do root. */
  id: string | null
  name: string
}

/**
 * A clínica que vem marcada para quem não é root. Logo depois de entrar: a padrão, senão a atual,
 * senão a primeira. Na troca dentro da sessão: a atual ("a ativa marcada").
 */
function preselected(clinics: UserClinic[], preferCurrent: boolean): Choice | null {
  const usable = clinics.filter(isUsableClinic)
  const current = usable.find((item) => item.isCurrent)
  const standard = usable.find((item) => item.isDefault)
  const clinic = (preferCurrent ? (current ?? standard) : (standard ?? current)) ?? usable[0]
  return clinic ? { id: clinic.id, name: clinic.name } : null
}

export function ChooseClinicPage() {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  // Veio do rodapé da sidebar: é a troca dentro da sessão, não a escolha de quem acabou de entrar.
  const isSwitching = (location.state as { switching?: boolean } | null)?.switching === true
  const profile = session.profile
  const isRoot = Boolean(profile?.isRoot)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search)
  const clinics = useUserClinics(isRoot ? { search: debouncedSearch, page } : {})
  // O que a pessoa marcou; enquanto não marca nada, vale a pré-seleção.
  const [picked, setPicked] = useState<Choice | null>(null)
  const [wantsDefault, setWantsDefault] = useState(false)
  const [entering, setEntering] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  if (!profile) return null
  // Com uma clínica só não há o que escolher: a porta de entrada decide o destino.
  if (!entering && !isRoot && profile.clinics.filter(isUsableClinic).length <= 1) {
    return <Navigate to="/" replace />
  }

  const rows = clinics.data?.data ?? []
  const meta = clinics.data?.meta
  const fallback: Choice | null = isRoot
    ? profile.activeClinic
      ? { id: profile.activeClinic.id, name: profile.activeClinic.name }
      : { id: null, name: '' }
    : preselected(rows, isSwitching)
  const choice = picked ?? fallback
  const chosenRow = choice ? rows.find((clinic) => clinic.id === choice.id) : undefined
  // Root não tem clínica padrão (RN-AUT-10); a que já é a padrão não precisa da pergunta.
  const offersDefault = !isRoot && Boolean(chosenRow) && !chosenRow?.isDefault
  const searching = isRoot && debouncedSearch.trim() !== ''

  function pick(value: string) {
    setProblem(null)
    setWantsDefault(false)
    if (value === GLOBAL_VIEW) {
      setPicked({ id: null, name: '' })
      return
    }
    const clinic = rows.find((item) => item.id === value)
    if (clinic) setPicked({ id: clinic.id, name: clinic.name })
  }

  // Na troca, escolher a clínica em que já se está só devolve a pessoa ao sistema.
  const currentClinicId = profile.activeClinic?.id ?? null
  const staysHere = isSwitching && Boolean(choice) && choice?.id === currentClinicId

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!choice || entering) return
    if (staysHere) {
      navigate('/', { replace: true })
      return
    }
    setEntering(true)
    setProblem(null)
    try {
      await session.switchClinic(choice.id, { isDefault: offersDefault && wantsDefault })
    } catch (error) {
      setProblem(errorMessage(error))
      setEntering(false)
      // A lista pode ter mudado (vínculo bloqueado, clínica arquivada): a tela mostra o estado atual.
      void clinics.refetch()
      return
    }
    navigate('/', { replace: true })
  }

  const label = !choice
    ? 'Escolha uma clínica'
    : choice.id === null
      ? staysHere
        ? 'Continuar na visão global'
        : 'Entrar na visão global'
      : staysHere
        ? `Continuar em ${choice.name}`
        : `Entrar em ${choice.name}`

  return (
    <AuthLayout title={isSwitching ? 'Trocar de clínica' : 'Escolher clínica'} onSubmit={submit}>
      {isRoot ? (
        <SearchInput
          className="mt-8 flex-none"
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Buscar por nome, cidade ou CNPJ"
          aria-label="Buscar clínica"
        />
      ) : null}

      {clinics.isPending ? (
        <div role="status" aria-label="Carregando" className="mt-6 grid gap-2">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-[66px] w-full" />
          ))}
        </div>
      ) : clinics.isError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível carregar as clínicas"
          description={errorMessage(clinics.error)}
          actions={
            <Button variant="outline" size="sm" onClick={() => void clinics.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <>
          <RadioGroup
            aria-label="Clínicas"
            className={isRoot ? 'mt-4' : 'mt-8'}
            value={choice ? (choice.id ?? GLOBAL_VIEW) : ''}
            onValueChange={pick}
            disabled={entering}
          >
            {isRoot && !searching ? (
              <ChoiceRow
                control={<Radio value={GLOBAL_VIEW} />}
                description="Todas as clínicas, só para leitura."
              >
                Visão global
              </ChoiceRow>
            ) : null}
            {rows.map((clinic) => {
              // Root entra em clínica bloqueada (é quem a desbloqueia); os demais, não.
              const isBlocked = clinic.status === 'blocked'
              const place = [clinic.city, clinic.state].filter(Boolean).join(', ')
              return (
                <ChoiceRow
                  key={clinic.id}
                  control={<Radio value={clinic.id} disabled={isBlocked && !isRoot} />}
                  description={
                    isBlocked && !isRoot
                      ? 'Acesso bloqueado. Fale com quem administra a clínica.'
                      : [isRoot ? null : roleLabel(clinic.role), place].filter(Boolean).join(' · ')
                  }
                  trailing={
                    isBlocked ? (
                      <StatusTag level="attention">{isRoot ? 'Bloqueada' : 'Bloqueado'}</StatusTag>
                    ) : clinic.isDefault ? (
                      <StatusTag level="waiting">Padrão</StatusTag>
                    ) : null
                  }
                >
                  {clinic.name}
                </ChoiceRow>
              )
            })}
          </RadioGroup>

          {rows.length === 0 && searching ? (
            <EmptyState
              className="mt-2"
              title="Nenhuma clínica encontrada"
              description={`A busca por "${debouncedSearch.trim()}" cobre nome, cidade e documento. Confira o que foi digitado.`}
              action={
                <Button variant="outline" size="sm" onClick={() => setSearch('')}>
                  Limpar busca
                </Button>
              }
            />
          ) : null}
          {rows.length === 0 && !searching ? (
            <EmptyState
              className="mt-2"
              title={isRoot ? 'Nenhuma clínica cadastrada' : 'Nenhuma clínica disponível'}
              description={
                isRoot
                  ? 'As clínicas aparecem aqui depois de criadas. Entre na visão global para criar a primeira.'
                  : 'Sua conta não tem clínica em que possa entrar. Fale com quem administra a clínica.'
              }
            />
          ) : null}
          {isRoot && meta && meta.totalPages > 1 ? (
            <ListFooter meta={meta} noun={['clínica', 'clínicas']} onPage={setPage} />
          ) : null}

          {offersDefault ? (
            <ChoiceRow
              bare
              className="mt-4 border-b-0 px-0"
              control={
                <Checkbox
                  checked={wantsDefault}
                  onCheckedChange={(checked) => setWantsDefault(checked === true)}
                  disabled={entering}
                />
              }
              description="Nas próximas vezes, esta vem marcada."
            >
              Usar esta como padrão
            </ChoiceRow>
          ) : null}
        </>
      )}

      {problem ? (
        <ActionError
          className="mt-6"
          title="Não foi possível entrar na clínica"
          description={problem}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7 whitespace-normal"
        disabled={!choice || !clinics.isSuccess}
        loading={entering}
        loadingLabel="Entrando…"
      >
        {clinics.isSuccess ? label : 'Escolha uma clínica'}
      </Button>
      {isSwitching ? (
        <button type="button" onClick={() => navigate(-1)} className={`${authLink} mt-5`}>
          Voltar sem trocar
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void session.signOut().then(() => navigate(routes.login))}
          className={`${authLink} mt-5`}
        >
          Sair e entrar com outra conta
        </button>
      )}
      <WaitPhrase moment="signing_in" active={entering} className="mt-4" />
    </AuthLayout>
  )
}
